// Реестр разделов оболочки. Порядок MAIN — порядок вкладок верхней полосы
// (README §1.1). Зоны меняют содержимое своих папок sections/<id>/, этот файл
// и App.tsx/shell/* не трогают.

import type { Section } from '../store/appStore'
import type { SectionDef, SectionModule } from './types'
import chats from './chats'
import feed from './feed'
import flow from './flow'
import tracker from './tracker'
import notes from './notes'
import messengers from './messengers'
import library from './library'
import account from './account'
import kb from './kb'
import draft from './draft'
import custom from './custom'
import orchestrator from './orchestrator'
import { withOverlay } from './custom/overlay'
import { useSectionsPoll } from './custom/store'
import { useAppStore } from '../store/appStore'

function def(id: string, title: string, icon: SectionDef['icon'], main: boolean, mod: SectionModule): SectionDef {
  return { ...mod, id, title, icon, main }
}

/** Встроенный раздел, который правится «✎» (оверлей §10.2, зона pages). */
function editable(id: string, title: string, icon: SectionDef['icon'], mod: SectionModule): SectionDef {
  return def(id, title, icon, true, withOverlay(id, title, mod))
}

export const SECTIONS: SectionDef[] = [
  editable('chats', 'Беседы', 'chat', chats),
  // голосовой ассистент: не «✎»-редактируемый — вид целиком живой, оверлею там нечего делать
  def('orchestrator', 'Оркестратор', 'mic', true, orchestrator),
  editable('feed', 'Лента', 'feed', feed),
  editable('flow', 'Конструктор', 'flow', flow),
  editable('tracker', 'Трекер', 'tracker', tracker),
  editable('notes', 'Заметки', 'folder', notes),
  editable('messengers', 'Мессенджеры', 'chat', messengers),
  editable('library', 'Библиотеки', 'library', library),
  def('account', 'Кабинет', 'person', false, account),
  def('kb', 'База знаний', 'book', false, kb),
]

/** Раздел-черновик (README §10): одна запись на все `draft:<id>`. */
export const DRAFT_SECTION: SectionDef = def('draft', 'новый раздел', 'draft', false, draft)

/** Опубликованные пользовательские разделы `custom:<id>` (§10, зона pages):
 *  одна запись модуля, определение на каждый id — лениво, с живым названием
 *  из серверного списка разделов. */
const CUSTOM: Map<string, SectionDef> = new Map()
function customSection(id: string): SectionDef {
  let found = CUSTOM.get(id)
  if (!found) {
    found = def(id, 'раздел', 'draft', false, custom)
    CUSTOM.set(id, found)
  }
  return found
}

const BY_ID = new Map(SECTIONS.map((s) => [s.id, s]))

/** Определение раздела по id из appStore.section. Неизвестное — Беседы. */
export function resolveSection(id: Section | string): SectionDef {
  if (id.startsWith('draft:')) return DRAFT_SECTION
  if (id.startsWith('custom:')) return customSection(id)
  return BY_ID.get(id) ?? SECTIONS[0]
}

export interface DynamicTab { id: Section; title: string; draft: boolean }

/** Вкладки опубликованных пользовательских разделов для верхней полосы и
 *  серверные метки «черновик» встроенных; опрос списка разделов — пока смонтирована полоса. */
export function useDynamicTabs(): { tabs: DynamicTab[]; drafted: Set<string> } {
  const list = useSectionsPoll()
  const tabs: DynamicTab[] = []
  const drafted = new Set<string>()
  for (const s of list) {
    if (s.builtin) {
      if (s.hasDraft) drafted.add(s.id)
      continue
    }
    if (!s.published) continue
    const id = `custom:${s.id}` as Section
    const d = customSection(id)
    d.title = s.title
    tabs.push({ id, title: s.title, draft: s.hasDraft })
  }
  // раздел, открытый по сохранённому экрану, но уже удалённый — вернуть в Беседы
  const section = useAppStore.getState().section
  if (section.startsWith('custom:') && list.length && !tabs.some((t) => t.id === section)) {
    queueMicrotask(() => useAppStore.getState().setSection('chats'))
  }
  return { tabs, drafted }
}

export const MAIN_TABS = SECTIONS.filter((s) => s.main)
